import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react';
import {Modal} from '@carbon/react';
import {CheckmarkFilled, ErrorFilled} from '@carbon/icons-react';
import {ReactComponent as XlsxIcon} from './xlsx-icon.svg';
import {NetworkContext} from '../lib/context';
import {UploadJob} from '../lib/types';
import './upload-status-modal.scss';

export type Props = {
    open: boolean;
    onClose: () => void;
};

const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

type CancelConfirm =
    | {type: 'single'; id: string; filename: string}
    | {type: 'all'; count: number};

type JobCardProps = {
    job: UploadJob;
    onRetry: (id: string) => void;
    onRemove: (id: string) => void;
    onCancel: (id: string, filename: string) => void;
};

const JobCard = ({job, onRetry, onRemove, onCancel}: JobCardProps) => {
    const isActive = job.phase === 'initializing' || job.phase === 'uploading' || job.phase === 'assembling';
    const isDone = job.phase === 'done';
    const isError = job.phase === 'error' || job.phase === 'interrupted';
    const isAssembling = job.phase === 'assembling';
    const canRetry = job.phase === 'error' && !!job.file;

    if (isError) {
        return (
            <li className="upload-job upload-job--error-notification" role="alert">
                <ErrorFilled size={16} className="upload-job__error-icon-left" />
                <div className="upload-job__error-body">
                    <span className="upload-job__error-title" title={job.filename}>{job.filename}</span>
                    <span className="upload-job__error-detail">
                        {job.errorMessage ?? 'Upload failed. Please try again.'}
                    </span>
                    <span className="upload-job__error-meta">{formatBytes(job.fileSize)}</span>
                </div>
                <div className="upload-job__error-actions">
                    {canRetry && (
                        <button
                            type="button"
                            className="upload-job__error-action-btn"
                            onClick={() => onRetry(job.id)}
                        >
                            Retry upload
                        </button>
                    )}
                    <button
                        type="button"
                        className="upload-job__error-action-btn"
                        onClick={() => onRemove(job.id)}
                    >
                        Remove
                    </button>
                </div>
            </li>
        );
    }

    return (
        <li className={`upload-job${isDone ? ' upload-job--done' : ''}`}>
            <XlsxIcon className="upload-job__icon" />
            <div className="upload-job__body">
                <span className="upload-job__filename" title={job.filename}>{job.filename}</span>
                <span className="upload-job__size">{formatBytes(job.fileSize)}</span>
                {isActive && (job.phase === 'initializing' || !!job.uploadId) && (
                    <div className="upload-job__progress-area">
                        <div className="upload-job__progress-wrap">
                            <div
                                className={`upload-job__progress-fill${isAssembling ? ' upload-job__progress-fill--assembling' : ''}`}
                                style={{width: `${job.progress}%`}}
                            />
                        </div>
                        <span className="upload-job__progress-pct">{job.progress}%</span>
                    </div>
                )}
            </div>
            <div className="upload-job__status-icon">
                {isDone && <CheckmarkFilled size={16} className="upload-job__done-icon" />}
                {isActive && (
                    <button
                        type="button"
                        className="upload-job__cancel-btn"
                        onClick={() => onCancel(job.id, job.filename)}
                    >
                        Cancel
                    </button>
                )}
            </div>
        </li>
    );
};

const UploadStatusModal = observer(({open, onClose}: Props) => {
    const store = useContext(NetworkContext);
    const [cancelConfirm, setCancelConfirm] = useState<CancelConfirm | null>(null);

    const handleClose = () => {
        store.clearCompletedJobs();
        onClose();
    };

    const jobs = store.uploadJobs;
    const doneCount = jobs.filter(j => j.phase === 'done').length;
    const errorCount = jobs.filter(j => j.phase === 'error' || j.phase === 'interrupted').length;
    const activeCount = store.activeUploadCount;

    const summaryText = activeCount > 0
        ? `Uploading ${activeCount} file${activeCount !== 1 ? 's' : ''}…`
        : (doneCount > 0 || errorCount > 0)
            ? [
                doneCount > 0 && `${doneCount} file${doneCount !== 1 ? 's' : ''} uploaded`,
                errorCount > 0 && `${errorCount} failed`,
            ].filter(Boolean).join(' · ')
            : 'No uploads in progress';

    const handleConfirmCancel = () => {
        if (!cancelConfirm) return;
        if (cancelConfirm.type === 'single') {
            store.cancelJob(cancelConfirm.id);
        } else {
            store.cancelAllJobs();
        }
        setCancelConfirm(null);
    };

    return (
        <>
            <Modal
                open={open}
                onRequestClose={handleClose}
                modalHeading="Upload Status"
                passiveModal
                size="sm"
                className="upload-status-modal-container"
            >
                <div className="upload-status-modal">
                    <div className="upload-status-modal__summary-row">
                        <span className="upload-status-modal__summary">{summaryText}</span>
                        <div className="upload-status-modal__row-actions">
                            {activeCount > 0 && (
                                <button
                                    type="button"
                                    className="upload-status-modal__clear-btn"
                                    onClick={() => setCancelConfirm({type: 'all', count: activeCount})}
                                >
                                    Cancel all
                                </button>
                            )}
                        </div>
                    </div>
                    {jobs.length === 0 ? (
                        <p className="upload-status-modal__empty">No upload jobs to display.</p>
                    ) : (
                        <ul className="upload-job-list" aria-label="Upload jobs">
                            {jobs.map(job => (
                                <JobCard
                                    key={job.id}
                                    job={job}
                                    onRetry={(id) => store.retryJob(id)}
                                    onRemove={(id) => store.removeJob(id)}
                                    onCancel={(id, filename) => setCancelConfirm({type: 'single', id, filename})}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            </Modal>

            <Modal
                open={cancelConfirm !== null}
                onRequestClose={() => setCancelConfirm(null)}
                onRequestSubmit={handleConfirmCancel}
                onSecondarySubmit={() => setCancelConfirm(null)}
                modalHeading={cancelConfirm?.type === 'all' ? 'Cancel all uploads?' : 'Cancel upload?'}
                primaryButtonText={cancelConfirm?.type === 'all' ? 'Cancel all' : 'Cancel upload'}
                secondaryButtonText="Keep uploading"
                danger
                size="sm"
            >
                {cancelConfirm?.type === 'single' ? (
                    <p>
                        Cancel uploading <strong>{cancelConfirm.filename}</strong>? It will be removed from the list.
                    </p>
                ) : (
                    <p>
                        Cancel all {cancelConfirm?.count} ongoing upload{cancelConfirm?.count !== 1 ? 's' : ''}? They will be removed from the list.
                    </p>
                )}
            </Modal>
        </>
    );
});

export default UploadStatusModal;
